import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createEPK, getEPK, updateEPK, uploadImage, getFileUrl } from '../lib/api';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Plus, Trash2, Upload, Save, X, GripVertical } from 'lucide-react';

const emptyRelease = () => ({ id: crypto.randomUUID(), title: '', date: '', format: 'Single', producer: '', description: '', link: '', spotify_embed_url: '', is_released: true });
const emptyStat = () => ({ value: '', label: '' });

export default function EPKEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [epk, setEpk] = useState({
    slug: '',
    basic_info: { artist_name: '', genre: '', origin: '', next_release: '', last_release: '', label: '' },
    short_bio: '',
    musical_background: '',
    releases_strategy: '',
    releases: [],
    youtube_videos: [''],
    gallery_images: [],
    stats: [
      { value: '', label: 'Veröffentlichte Tracks' },
      { value: '', label: 'Musikvideos' },
      { value: '', label: 'Konzerte' },
      { value: '', label: 'Instagram Follower' },
      { value: '', label: 'Monatliche Hörer' },
      { value: '', label: 'All-time Streams' },
    ],
    social_links: { instagram: '', youtube: '', spotify: '', apple_music: '', email: '' },
  });

  useEffect(() => {
    if (!isNew) {
      getEPK(id).then(({ data }) => setEpk(data)).catch(() => navigate('/admin'));
    }
  }, [id, isNew, navigate]);

  const updateField = (path, value) => {
    setEpk(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        obj = obj[k];
      }
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await createEPK(epk);
        navigate(`/admin/epk/${data.id}`);
      } else {
        await updateEPK(id, epk);
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const result = await uploadImage(file);
        setEpk(prev => ({
          ...prev,
          gallery_images: [...prev.gallery_images, {
            id: crypto.randomUUID(),
            storage_path: result.storage_path,
            original_filename: result.original_filename,
          }],
        }));
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (imageId) => {
    setEpk(prev => ({ ...prev, gallery_images: prev.gallery_images.filter(img => img.id !== imageId) }));
  };

  const addRelease = () => setEpk(prev => ({ ...prev, releases: [...prev.releases, emptyRelease()] }));
  const removeRelease = (idx) => setEpk(prev => ({ ...prev, releases: prev.releases.filter((_, i) => i !== idx) }));

  const addYouTube = () => setEpk(prev => ({ ...prev, youtube_videos: [...prev.youtube_videos, ''] }));
  const removeYouTube = (idx) => setEpk(prev => ({ ...prev, youtube_videos: prev.youtube_videos.filter((_, i) => i !== idx) }));

  const addStat = () => setEpk(prev => ({ ...prev, stats: [...prev.stats, emptyStat()] }));
  const removeStat = (idx) => setEpk(prev => ({ ...prev, stats: prev.stats.filter((_, i) => i !== idx) }));

  const LabeledInput = ({ label, value, onChange, textarea, ...props }) => (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold mb-1 block" style={{ fontFamily: 'var(--font-heading)' }}>{label}</label>
      {textarea ? (
        <Textarea value={value || ''} onChange={onChange} className="admin-input min-h-[120px]" {...props} />
      ) : (
        <Input value={value || ''} onChange={onChange} className="admin-input" {...props} />
      )}
    </div>
  );

  return (
    <div className="admin-layout">
      <nav className="admin-nav">
        <div className="flex items-center gap-4">
          <button data-testid="back-btn" onClick={() => navigate('/admin')} className="flex items-center gap-1" style={{ color: 'var(--admin-bg)' }}>
            <ArrowLeft size={18} /> <span className="text-sm uppercase" style={{ fontFamily: 'var(--font-heading)' }}>ZURÜCK</span>
          </button>
          <img src="/AC_logo_longbeige.png" alt="ALL CAPS" className="h-6" />
        </div>
        <button data-testid="save-epk-btn" onClick={handleSave} disabled={saving} className="admin-btn-primary" style={{ background: 'var(--admin-bg)', color: 'var(--admin-primary)' }}>
          <span className="flex items-center gap-2"><Save size={16} /> {saving ? 'SPEICHERN...' : 'SPEICHERN'}</span>
        </button>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-black mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
          {isNew ? 'NEUES EPK ERSTELLEN' : 'EPK BEARBEITEN'}
        </h1>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="w-full justify-start bg-transparent border-b border-[var(--admin-primary)] p-0 mb-6 h-auto gap-0">
            {[
              ['basic', 'BASICS'],
              ['bio', 'BIOGRAFIE'],
              ['releases', 'RELEASES'],
              ['media', 'MEDIEN'],
              ['stats', 'FAKTEN'],
              ['social', 'KONTAKT'],
            ].map(([val, label]) => (
              <TabsTrigger
                key={val}
                value={val}
                data-testid={`tab-${val}`}
                className="px-4 py-2 text-sm uppercase tracking-wider data-[state=active]:bg-[var(--admin-primary)] data-[state=active]:text-[var(--admin-bg)] border-0"
                style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* BASICS TAB */}
          <TabsContent value="basic">
            <div className="admin-card space-y-4">
              <LabeledInput label="URL-SLUG" value={epk.slug} onChange={e => updateField('slug', e.target.value)} data-testid="input-slug" placeholder="z.B. waleed-2026" />
              <div className="grid grid-cols-2 gap-4">
                <LabeledInput label="ARTIST NAME" value={epk.basic_info.artist_name} onChange={e => updateField('basic_info.artist_name', e.target.value)} data-testid="input-artist-name" />
                <LabeledInput label="GENRE" value={epk.basic_info.genre} onChange={e => updateField('basic_info.genre', e.target.value)} data-testid="input-genre" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <LabeledInput label="HERKUNFT" value={epk.basic_info.origin} onChange={e => updateField('basic_info.origin', e.target.value)} data-testid="input-origin" />
                <LabeledInput label="LABEL" value={epk.basic_info.label} onChange={e => updateField('basic_info.label', e.target.value)} data-testid="input-label" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <LabeledInput label="NÄCHSTES RELEASE" value={epk.basic_info.next_release} onChange={e => updateField('basic_info.next_release', e.target.value)} data-testid="input-next-release" />
                <LabeledInput label="LETZTES RELEASE" value={epk.basic_info.last_release} onChange={e => updateField('basic_info.last_release', e.target.value)} data-testid="input-last-release" />
              </div>
            </div>
          </TabsContent>

          {/* BIO TAB */}
          <TabsContent value="bio">
            <div className="admin-card space-y-4">
              <LabeledInput label="KURZBIOGRAFIE" textarea value={epk.short_bio} onChange={e => updateField('short_bio', e.target.value)} data-testid="input-short-bio" />
              <LabeledInput label="MUSIKALISCHER WERDEGANG" textarea value={epk.musical_background} onChange={e => updateField('musical_background', e.target.value)} data-testid="input-musical-background" />
              <LabeledInput label="RELEASES & STRATEGIE 2026" textarea value={epk.releases_strategy} onChange={e => updateField('releases_strategy', e.target.value)} data-testid="input-releases-strategy" />
            </div>
          </TabsContent>

          {/* RELEASES TAB */}
          <TabsContent value="releases">
            <div className="space-y-4">
              {epk.releases.map((rel, idx) => (
                <div key={rel.id} className="admin-card" data-testid={`release-card-${idx}`}>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-bold uppercase" style={{ fontFamily: 'var(--font-heading)' }}>RELEASE #{idx + 1}</h3>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2" data-testid={`release-status-toggle-${idx}`}>
                        <label className="text-xs uppercase tracking-wider font-semibold" style={{ fontFamily: 'var(--font-heading)', color: rel.is_released !== false ? 'var(--admin-primary)' : 'var(--admin-text-muted)' }}>
                          {rel.is_released !== false ? 'RELEASED' : 'UPCOMING'}
                        </label>
                        <Switch
                          checked={rel.is_released !== false}
                          onCheckedChange={(checked) => updateField(`releases.${idx}.is_released`, checked)}
                        />
                      </div>
                      <button data-testid={`remove-release-${idx}`} onClick={() => removeRelease(idx)} className="p-1 hover:text-[var(--admin-danger)]">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <LabeledInput label="TITEL" value={rel.title} onChange={e => updateField(`releases.${idx}.title`, e.target.value)} />
                    <LabeledInput label="DATUM" value={rel.date} onChange={e => updateField(`releases.${idx}.date`, e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <LabeledInput label="FORMAT" value={rel.format} onChange={e => updateField(`releases.${idx}.format`, e.target.value)} />
                    <LabeledInput label="PRODUZENT" value={rel.producer} onChange={e => updateField(`releases.${idx}.producer`, e.target.value)} />
                  </div>
                  <LabeledInput label="BESCHREIBUNG" textarea value={rel.description} onChange={e => updateField(`releases.${idx}.description`, e.target.value)} />
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <LabeledInput label="LINK (BFAN.LINK ETC.)" value={rel.link} onChange={e => updateField(`releases.${idx}.link`, e.target.value)} />
                    <LabeledInput label="SPOTIFY EMBED URL" value={rel.spotify_embed_url} onChange={e => updateField(`releases.${idx}.spotify_embed_url`, e.target.value)} placeholder="https://open.spotify.com/track/..." />
                  </div>
                </div>
              ))}
              <button data-testid="add-release-btn" onClick={addRelease} className="admin-btn-outline w-full">
                <span className="flex items-center gap-2 justify-center"><Plus size={16} /> RELEASE HINZUFÜGEN</span>
              </button>
            </div>
          </TabsContent>

          {/* MEDIA TAB */}
          <TabsContent value="media">
            <div className="space-y-6">
              {/* Gallery */}
              <div className="admin-card">
                <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'var(--font-heading)' }}>BILDERGALERIE</h3>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {epk.gallery_images.map((img) => (
                    <div key={img.id} className="relative group" data-testid={`gallery-image-${img.id}`}>
                      <img
                        src={getFileUrl(img.storage_path)}
                        alt={img.original_filename}
                        className="w-full h-32 object-cover border border-[var(--admin-primary)]"
                      />
                      <button
                        onClick={() => removeGalleryImage(img.id)}
                        className="absolute top-1 right-1 p-1 bg-[var(--admin-danger)] text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <label className="admin-btn-outline cursor-pointer inline-flex items-center gap-2" data-testid="upload-images-btn">
                  <Upload size={16} /> {uploading ? 'HOCHLADEN...' : 'BILDER HOCHLADEN'}
                  <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              </div>

              {/* YouTube */}
              <div className="admin-card">
                <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'var(--font-heading)' }}>YOUTUBE VIDEOS</h3>
                {epk.youtube_videos.map((url, idx) => (
                  <div key={idx} className="flex gap-2 mb-2" data-testid={`youtube-input-${idx}`}>
                    <Input
                      value={url}
                      onChange={e => updateField(`youtube_videos.${idx}`, e.target.value)}
                      className="admin-input flex-1"
                      placeholder="https://youtube.com/watch?v=..."
                    />
                    <button onClick={() => removeYouTube(idx)} className="p-2 hover:text-[var(--admin-danger)]"><Trash2 size={16} /></button>
                  </div>
                ))}
                <button data-testid="add-youtube-btn" onClick={addYouTube} className="admin-btn-outline mt-2">
                  <span className="flex items-center gap-2"><Plus size={16} /> VIDEO HINZUFÜGEN</span>
                </button>
              </div>
            </div>
          </TabsContent>

          {/* STATS TAB */}
          <TabsContent value="stats">
            <div className="admin-card">
              <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'var(--font-heading)' }}>DATEN & FAKTEN</h3>
              {epk.stats.map((stat, idx) => (
                <div key={idx} className="flex gap-2 mb-2" data-testid={`stat-row-${idx}`}>
                  <Input value={stat.value} onChange={e => updateField(`stats.${idx}.value`, e.target.value)} className="admin-input w-32" placeholder="+40" />
                  <Input value={stat.label} onChange={e => updateField(`stats.${idx}.label`, e.target.value)} className="admin-input flex-1" placeholder="Veröffentlichte Tracks" />
                  <button onClick={() => removeStat(idx)} className="p-2 hover:text-[var(--admin-danger)]"><Trash2 size={16} /></button>
                </div>
              ))}
              <button data-testid="add-stat-btn" onClick={addStat} className="admin-btn-outline mt-2">
                <span className="flex items-center gap-2"><Plus size={16} /> FAKT HINZUFÜGEN</span>
              </button>
            </div>
          </TabsContent>

          {/* SOCIAL TAB */}
          <TabsContent value="social">
            <div className="admin-card space-y-4">
              <LabeledInput label="INSTAGRAM URL" value={epk.social_links.instagram} onChange={e => updateField('social_links.instagram', e.target.value)} data-testid="input-instagram" placeholder="https://instagram.com/..." />
              <LabeledInput label="YOUTUBE URL" value={epk.social_links.youtube} onChange={e => updateField('social_links.youtube', e.target.value)} data-testid="input-youtube" placeholder="https://youtube.com/..." />
              <LabeledInput label="SPOTIFY URL" value={epk.social_links.spotify} onChange={e => updateField('social_links.spotify', e.target.value)} data-testid="input-spotify" placeholder="https://open.spotify.com/artist/..." />
              <LabeledInput label="APPLE MUSIC URL" value={epk.social_links.apple_music} onChange={e => updateField('social_links.apple_music', e.target.value)} data-testid="input-apple-music" placeholder="https://music.apple.com/..." />
              <LabeledInput label="E-MAIL" value={epk.social_links.email} onChange={e => updateField('social_links.email', e.target.value)} data-testid="input-email" placeholder="kontakt@artist.de" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
